// src/app/services/websocket.service.ts
import { Injectable } from '@angular/core';
import { webSocket, WebSocketSubject } from 'rxjs/webSocket';
import { BehaviorSubject, Observable, Subject, Subscription, of, timer } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

interface NotificationMessage {
  message?: string;
  error?: string;
  type?: string;
  // d'autres champs possibles selon la structure envoyée par le serveur
}

interface PrestationActeMessage {
  action?: string;
  instance?: any;
  [key: string]: any;
}

export interface PrestationActeMessageBeneficiaire {
  action: 'created' | 'updated' | 'deleted' | string;
  id: number;
  model: string;
  designation?: string;
  status?: string;
}

export interface InitialDataMessage {
  type: 'initial_data';
  data: PrestationActeMessage[];
}

@Injectable({
  providedIn: 'root'
})
export class WebSocketService {
  private socket$: WebSocketSubject<any>;
  private messagesSubject = new Subject<any>();
  public messages$ = this.messagesSubject.asObservable();

  private notificationSocket$: WebSocketSubject<any>;
  private notificationSubject = new Subject<any>();
  public notifications$ = this.notificationSubject.asObservable();

  private readonly BASE_WS_URL = environment.wsUrl;
  private socket: WebSocket | null = null;



  constructor() {}

  // Connect to chat
  connectChat(roomName: string = 'general'): void {
    try {
      const url = `${this.BASE_WS_URL}/ws/chat/${roomName}/`;
      console.log('Connecting to chat WebSocket:', url);
      
      this.socket$ = webSocket(url);
      
      this.socket$.pipe(
        catchError(error => {
          console.error('Chat WebSocket connection failed:', error);
          this.messagesSubject.next({ error: 'Connection failed' });
          return of(null);
        })
      ).subscribe({
        next: (msg) => {
          if (msg) {
            this.messagesSubject.next(msg);
          }
        },
        error: (err) => {
          console.error('Chat WebSocket error:', err);
          this.messagesSubject.next({ error: err.message });
        },
        complete: () => {
          console.log('Chat WebSocket connection closed');
          this.messagesSubject.next({ message: 'Connection closed' });
        }
      });

    } catch (error) {
      console.error('Failed to create chat WebSocket connection:', error);
      this.messagesSubject.next({ error: 'Failed to connect' });
    }
  }

  // Connect to notifications
  connectNotifications(): void {
    try {
      const url = `${this.BASE_WS_URL}/ws/notifications/`;
      console.log('Connecting to notifications WebSocket:', url);
      
      this.notificationSocket$ = webSocket(url);
      
      this.notificationSocket$.pipe(
        catchError(error => {
          console.error('Notifications WebSocket connection failed:', error);
          this.notificationSubject.next({ error: 'Connection failed' });
          return of(null);
        })
      ).subscribe({
        next: (msg) => {
          if (msg) {
            this.notificationSubject.next(msg);
          }
        },
        error: (err) => {
          console.error('Notifications WebSocket error:', err);
          this.notificationSubject.next({ error: err.message });
        },
        complete: () => {
          console.log('Notifications WebSocket connection closed');
          this.notificationSubject.next({ message: 'Connection closed' });
        }
      });

    } catch (error) {
      console.error('Failed to create notifications WebSocket connection:', error);
      this.notificationSubject.next({ error: 'Failed to connect' });
    }
  }

  sendChatMessage(message: string, username: string = 'User'): void {
    if (this.socket$ && !this.socket$.closed) {
      this.socket$.next({
        message: message,
        username: username
      });
    } else {
      console.error('Chat WebSocket is not connected');
    }
  }

  sendNotification(message: string): void {
    if (this.notificationSocket$ && !this.notificationSocket$.closed) {
      this.notificationSocket$.next({
        message: message
      });
    } else {
      console.error('Notifications WebSocket is not connected');
    }
  }

  disconnect(): void {
    if (this.socket$) {
      this.socket$.complete();
    }
    if (this.notificationSocket$) {
      this.notificationSocket$.complete();
    }
  }


  // Connect to user-specific notifications
  private userNotificationSocket$: WebSocketSubject<any>;
  private userNotificationSubject = new Subject<any>();
  public userNotifications$ = this.userNotificationSubject.asObservable();

  // Connect to user-specific notifications
  connectUserNotifications(): void {
    try {
      const url = `${this.BASE_WS_URL}/ws/user/notifications/`;
      console.log('Connecting to user notifications WebSocket:', url);
      
      this.userNotificationSocket$ = webSocket(url);
      
      this.userNotificationSocket$.pipe(
        catchError(error => {
          console.error('User notifications WebSocket connection failed:', error);
          this.userNotificationSubject.next({ error: 'Connection failed', type: 'connection_error' });
          return of(null);
        })
      ).subscribe({
        next: (msg) => {
          if (msg) {
            this.userNotificationSubject.next(msg);
          }
        },
        error: (err) => {
          console.error('User notifications WebSocket error:', err);
          this.userNotificationSubject.next({ error: err.message, type: 'connection_error' });
        },
        complete: () => {
          console.log('User notifications WebSocket connection closed');
          this.userNotificationSubject.next({ type: 'connection_closed' });
        }
      });

    } catch (error) {
      console.error('Failed to create user notifications WebSocket connection:', error);
      this.userNotificationSubject.next({ error: 'Failed to connect', type: 'connection_error' });
    }
  }

  connectRandomUserSocket() {
    const socket = new  WebSocket(`${this.BASE_WS_URL}/ws/random-users/`);

    socket.onopen = () => {
        console.log("Connected ✅");
        // Ask server for 2 random users
        socket.send(JSON.stringify({ action: "get_users" }));
    };

    socket.onmessage = (event) => {
        const data = JSON.parse(event.data);
        if (data.users) {
            console.log("Randomly selected users:", data.users);
        } else if (data.message) {
            console.log(data.message);
        } else if (data.error) {
            console.error("Error:", data.error);
        }
    };

    socket.onclose = () => {
        console.log("Disconnected ❌");
    };
}

connectRandomUserSocketPeriodic() {
  this.socket = new WebSocket(`${this.BASE_WS_URL}/ws/random-users-periodic/`);

  this.socket.onopen = () => {
    console.log("Connected ✅");
  };

  this.socket.onmessage = (event) => {
    const data = JSON.parse(event.data);
    if (data.users) {
      console.log(`[${data.action}] Randomly selected users:`, data.users);
    } else if (data.message) {
      console.log(data.message);
    } else if (data.error) {
      console.error("Error:", data.error);
    }
  };

  this.socket.onclose = (event) => {
    console.log("Disconnected ❌", event.code, event.reason);
  };
}

/** ✅ Call this to stop periodic updates */
  stopPeriodicUpdates() {
    console.log('stopping')
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      console.log('inside')
      this.socket.send(JSON.stringify({ action: "stop" }));
      console.log("⏹ Stop command sent to server");
    }
  }



  // Sujet WebSocketSubject pour la connexion persistante
  private pendingNotificationsSocket$: WebSocketSubject<any> | null = null;

  // Subject interne pour émettre les messages reçus ou les erreurs
  private pendingNotificationsSubject = new Subject<NotificationMessage>();

  // Observable public auquel les composants peuvent s'abonner
  public pendingNotifications$ = this.pendingNotificationsSubject.asObservable();

  connectPendingNotifications(): void {
    try {
      const url = `${this.BASE_WS_URL}/ws/notifications/pending/`;
      console.log('Connecting to pending notifications WebSocket:', url);

      // Création du WebSocketSubject
      this.pendingNotificationsSocket$ = webSocket(url);

      // On s'abonne aux messages via le pipe pour gérer les erreurs
      this.pendingNotificationsSocket$
        .pipe(
          // catchError permet de capturer les erreurs du flux WebSocket
          catchError(error => {
            console.error('Pending notifications WebSocket connection failed:', error);
            // On informe le sujet interne qu'il y a eu une erreur
            this.pendingNotificationsSubject.next({ error: 'Connection failed', type: 'connection_error' });
            // Retourne un observable neutre pour ne pas casser la chaîne
            return of(null);
          })
        )
        .subscribe({
          next: (msg) => {
            // Chaque message non-null reçu est émis vers les abonnés
            if (msg) {
              this.pendingNotificationsSubject.next(msg);
            }
          },
          error: (err) => {
            // En cas d'erreur sur le WebSocket
            console.error('Pending notifications WebSocket error:', err);
            this.pendingNotificationsSubject.next({ error: err.message || 'Unknown error', type: 'connection_error' });
          },
          complete: () => {
            // Lorsque la connexion est fermée proprement
            console.log('Pending notifications WebSocket connection closed');
            this.pendingNotificationsSubject.next({ type: 'connection_closed' });
          }
        });

    } catch (error) {
      // En cas d'exception lors de la création du socket
      console.error('Failed to create pending notifications WebSocket connection:', error);
      this.pendingNotificationsSubject.next({ error: 'Failed to connect', type: 'connection_error' });
    }
  }

  /**
   * Méthode pour fermer la connexion WebSocket proprement.
   * À appeler lorsque le composant est détruit ou que la connexion n'est plus nécessaire.
   */
  disconnectPendingNotifications(): void {
    if (this.pendingNotificationsSocket$) {
      // On complète le sujet, ce qui ferme la connexion
      this.pendingNotificationsSocket$.complete();
      this.pendingNotificationsSocket$ = null;
    }
  }

  /**
   * Retourne un Observable des messages (et des états d'erreur/fermeture)
   * auquel les composants peuvent s'abonner pour recevoir les notifications en attente.
   */
  getPendingNotificationsStream(): Observable<NotificationMessage> {
    return this.pendingNotifications$;
  }




  private prestationActeSocket!: WebSocket;
  private prestationActeUrl = `${this.BASE_WS_URL}/ws/prestation_acte_updates/`;
  private prestationActeReconnectInterval = 2000;  // ms delay base for reconnection
  private prestationActeMaxReconnectAttempts = 10;
  private prestationActeReconnectAttempts = 0;
  private prestationActeManualClose = false;

  private prestationActeMessageSubject = new Subject<PrestationActeMessage>();
  public prestationActeMessages$ = this.prestationActeMessageSubject.asObservable();

  private prestationActeStatusSubject = new BehaviorSubject<'CONNECTING'|'OPEN'|'CLOSED'>('CLOSED');
  public prestationActeStatus$ = this.prestationActeStatusSubject.asObservable();

  private prestationActeReconnectSub?: Subscription;


  prestationActeConnect(): void {
    this.prestationActeStatusSubject.next('CONNECTING');
    this.prestationActeSocket = new WebSocket(this.prestationActeUrl);

    this.prestationActeSocket.onopen = (event: Event) => {
      this.prestationActeReconnectAttempts = 0;
      this.prestationActeStatusSubject.next('OPEN');
      console.log('[WebsocketService] prestationActe: connected');
    };

    this.prestationActeSocket.onmessage = (event: MessageEvent) => {
      this.prestationActeOnMessage(event);
    };

    this.prestationActeSocket.onerror = (event: Event) => {
      console.error('[WebsocketService] prestationActe: error', event);
    };

    this.prestationActeSocket.onclose = (event: CloseEvent) => {
      console.warn('[WebsocketService] prestationActe: closed', event);
      this.prestationActeStatusSubject.next('CLOSED');
      if (!this.prestationActeManualClose) {
        this.prestationActeTryReconnect();
      }
    };
  }

  prestationActeOnMessage(event: MessageEvent): void {
    try {
      const data: PrestationActeMessage = JSON.parse(event.data);
      this.prestationActeMessageSubject.next(data);
    } catch (err) {
      console.error('[WebsocketService] prestationActe: error parsing message', err);
    }
  }

  prestationActeSend(message: any): void {
    if (this.prestationActeSocket && this.prestationActeSocket.readyState === WebSocket.OPEN) {
      this.prestationActeSocket.send(JSON.stringify(message));
    } else {
      console.warn('[WebsocketService] prestationActe: cannot send, socket not open', message);
    }
  }

  prestationActeClose(): void {
    this.prestationActeManualClose = true;
    this.prestationActeSocket.close();
  }

  private prestationActeTryReconnect(): void {
    if (this.prestationActeReconnectAttempts >= this.prestationActeMaxReconnectAttempts) {
      console.error('[WebsocketService] prestationActe: max reconnect attempts reached');
      return;
    }
    this.prestationActeReconnectAttempts++;
    const delay = this.prestationActeReconnectInterval * this.prestationActeReconnectAttempts;
    console.log(`[WebsocketService] prestationActe: reconnect attempt ${this.prestationActeReconnectAttempts} in ${delay} ms`);

    this.prestationActeReconnectSub = timer(delay).subscribe(() => {
      this.prestationActeConnect();
    });
  }


  //SOCKET POUR CHAQUE PRESTATAION DU BENEFICIAIRE

   // Beneficiaire-specific WebSocket
  private beneficiairePrestationSocket!: WebSocket;
  private beneficiairePrestationUrl = `${this.BASE_WS_URL}/ws/beneficiaire-prestations/`;
  private beneficiairePrestationReconnectInterval = 2000;
  private beneficiairePrestationMaxReconnectAttempts = 10;
  private beneficiairePrestationReconnectAttempts = 0;
  private beneficiairePrestationManualClose = false;
  private currentBeneficiaireId: number | null = null;

  private beneficiairePrestationMessageSubject = new Subject<PrestationActeMessage | InitialDataMessage>();
  public beneficiairePrestationMessages$ = this.beneficiairePrestationMessageSubject.asObservable();

  private beneficiairePrestationStatusSubject = new BehaviorSubject<'CONNECTING'|'OPEN'|'CLOSED'>('CLOSED');
  public beneficiairePrestationStatus$ = this.beneficiairePrestationStatusSubject.asObservable();

  private beneficiairePrestationReconnectSub?: Subscription;

  // Beneficiaire-specific methods
  beneficiairePrestationConnect(beneficiaireId: number): void {
    this.currentBeneficiaireId = beneficiaireId;
    this.beneficiairePrestationManualClose = false;
    this.beneficiairePrestationStatusSubject.next('CONNECTING');
    
    // Using URL pattern
    const url = `${this.beneficiairePrestationUrl}${beneficiaireId}/`;
    
    this.beneficiairePrestationSocket = new WebSocket(url);

    this.beneficiairePrestationSocket.onopen = (event: Event) => {
      this.beneficiairePrestationReconnectAttempts = 0;
      this.beneficiairePrestationStatusSubject.next('OPEN');
      console.log('[WebsocketService] beneficiairePrestation: connected for ID', beneficiaireId);
    };

    this.beneficiairePrestationSocket.onmessage = (event: MessageEvent) => {
      this.beneficiairePrestationOnMessage(event);
    };

    this.beneficiairePrestationSocket.onerror = (event: Event) => {
      console.error('[WebsocketService] beneficiairePrestation: error', event);
    };

    this.beneficiairePrestationSocket.onclose = (event: CloseEvent) => {
      console.warn('[WebsocketService] beneficiairePrestation: closed', event);
      this.beneficiairePrestationStatusSubject.next('CLOSED');
      if (!this.beneficiairePrestationManualClose && this.currentBeneficiaireId) {
        this.beneficiairePrestationTryReconnect();
      }
    };
  }

  beneficiairePrestationOnMessage(event: MessageEvent): void {
    try {
      const data: PrestationActeMessageBeneficiaire | InitialDataMessage = JSON.parse(event.data);
      this.beneficiairePrestationMessageSubject.next(data);
    } catch (err) {
      console.error('[WebsocketService] beneficiairePrestation: error parsing message', err);
    }
  }

  beneficiairePrestationSend(message: any): void {
    if (this.beneficiairePrestationSocket && this.beneficiairePrestationSocket.readyState === WebSocket.OPEN) {
      this.beneficiairePrestationSocket.send(JSON.stringify(message));
    } else {
      console.warn('[WebsocketService] beneficiairePrestation: cannot send, socket not open', message);
    }
  }

  beneficiairePrestationClose(): void {
    this.beneficiairePrestationManualClose = true;
    this.currentBeneficiaireId = null;
    if (this.beneficiairePrestationSocket) {
      this.beneficiairePrestationSocket.close();
    }
    if (this.beneficiairePrestationReconnectSub) {
      this.beneficiairePrestationReconnectSub.unsubscribe();
    }
  }

  private beneficiairePrestationTryReconnect(): void {
    if (this.beneficiairePrestationReconnectAttempts >= this.beneficiairePrestationMaxReconnectAttempts) {
      console.error('[WebsocketService] beneficiairePrestation: max reconnect attempts reached');
      return;
    }
    if (!this.currentBeneficiaireId) {
      console.error('[WebsocketService] beneficiairePrestation: no beneficiaire ID for reconnection');
      return;
    }
    
    this.beneficiairePrestationReconnectAttempts++;
    const delay = this.beneficiairePrestationReconnectInterval * this.beneficiairePrestationReconnectAttempts;
    console.log(`[WebsocketService] beneficiairePrestation: reconnect attempt ${this.beneficiairePrestationReconnectAttempts} in ${delay} ms`);

    this.beneficiairePrestationReconnectSub = timer(delay).subscribe(() => {
      this.beneficiairePrestationConnect(this.currentBeneficiaireId!);
    });
  }

  beneficiairePrestationChange(newBeneficiaireId: number): void {
    if (this.currentBeneficiaireId === newBeneficiaireId) {
      return;
    }
    this.beneficiairePrestationClose();
    this.beneficiairePrestationConnect(newBeneficiaireId);
  }

  getCurrentBeneficiaireId(): number | null {
    return this.currentBeneficiaireId;
  }


  logBeneficiairePrestationStatus(): void {
  console.log('[WebsocketService] Beneficiaire Prestation Status:', JSON.stringify({
    currentBeneficiaireId: this.currentBeneficiaireId,
    status: this.beneficiairePrestationStatusSubject.getValue(),
    socketReadyState: this.beneficiairePrestationSocket?.readyState
  }, null, 2));
}

  ngOnDestroy(): void {
    this.prestationActeClose();
    this.prestationActeMessageSubject.complete();
    this.prestationActeStatusSubject.complete();
    this.prestationActeReconnectSub?.unsubscribe();
    this.beneficiairePrestationClose();
    if (this.beneficiairePrestationReconnectSub) {
      this.beneficiairePrestationReconnectSub.unsubscribe();
    }
  }

  

}